import * as FluxUtils from 'flux/utils';
import { ActionConstants } from '../constants/ActionConstants';
import { AppConfigurationStore, AppConfiguration } from './AppConfigurationStore';
import { PersistentStore, MeasurementUnitType } from './PersistentStore';
import { AppDispatcher, DispatcherBaseAction } from '../dispatcher/AppDispatcher';
import { SceneActions } from '../actions/SceneActions';

import utils = egr.wcf.utils;
import { ColorRGB } from '../utils/GlobalHelper';
import { ProjectManagerStore } from './ProjectManagerStore';

export type GroundType = 'solid' | 'nofloor' | 'grid' | 'tiledfloor' | 'woodenfloor';

export interface FloorPlanSettings {
    visible: boolean;
    width: number;
    height: number;
    opacity: number;
    colorReduction: number;
    brightness: number;
}

// The values of the existing entries must not be changed!!!
// The state will be saved as json and the store must be able to restore
// the data saved with previous versions!
export interface ProjectSettingsStoreState {
    floorPlan: FloorPlanSettings | null;
    dataLanguage: string;
    unitOfMeasurement: MeasurementUnitType;
    articleListPrices: 'show' | 'hide';
    currency: string;
    groundColor: ColorRGB;
    groundType: GroundType;
    frontWalls: 'visible' | 'invisible';
}

class ProjectSettingsStoreClass extends FluxUtils.ReduceStore<ProjectSettingsStoreState, unknown> {
    protected get currency(): string {
        const currencyByParameter: string | null = AppConfigurationStore.getValue('currency', null);
        if (currencyByParameter && AppConfiguration.DEFAUL_CURRENCY_MAP.has(currencyByParameter)) {
            return currencyByParameter;
        }
        return PersistentStore.getState().currency;
    }

    public defaultGroundColor: ColorRGB = {r: 211, g: 206, b: 196};

    public isChangeable(property: 'currency'): boolean {
        return AppConfigurationStore.getValue(property, null) == null;
    }

    public getInitialState(): ProjectSettingsStoreState {
        return {
            floorPlan: null,
            dataLanguage: PersistentStore.getState().dataLanguage,
            unitOfMeasurement: PersistentStore.getState().unitOfMeasurement,
            articleListPrices: PersistentStore.getState().articleListPrices,
            currency: this.currency,
            groundColor: this.defaultGroundColor,
            groundType: 'solid',
            frontWalls: 'invisible',
        };
    }

    public reduce(state: ProjectSettingsStoreState, action: DispatcherBaseAction): ProjectSettingsStoreState {
        const newState: ProjectSettingsStoreState = this.internalReduce(state, action);

        if (
            state !== newState &&
            action.actionType !== ActionConstants.RESET_PROJECT_RELATED_SETTINGS &&
            (
                !AppConfigurationStore.getState().projectManagerAvailable ||
                ProjectManagerStore.getCurrentProject() != null
            )
        ) {
            setTimeout(SceneActions.markSceneAsChanged, 0);
        }

        return newState;
    }

    public getLengthUnit(): utils.number.LengthUnit {
        const state: ProjectSettingsStoreState = this.getState();
        switch (state.unitOfMeasurement) {
            case 'cm':
                return utils.number.LengthUnit.Centimeter;
            case 'mm':
                return utils.number.LengthUnit.Millimeter;
            case 'feet':
                return utils.number.LengthUnit.Feet;
            case 'inch':
                return utils.number.LengthUnit.Inch;
            default:
                return utils.number.LengthUnit.Meter;
        }
    }

    public getFloorPlanVisibility(): boolean {
        const state: ProjectSettingsStoreState = this.getState();
        return state.floorPlan != null && state.floorPlan.visible === true;
    }

    protected internalReduce(state: ProjectSettingsStoreState, action: DispatcherBaseAction): ProjectSettingsStoreState {
        // Note: this function should be `pure` -> we have to return a copy
        // of the current state if some property was changed

        switch (action.actionType) {
            case ActionConstants.RESET_PROJECT_RELATED_SETTINGS:
                return jQuery.extend({}, this.getInitialState());
            case ActionConstants.SET_GROUND_TYPE:
                return jQuery.extend({}, state, { groundType: action.value });
            case ActionConstants.SET_GROUND_COLOR:
                return jQuery.extend({}, state, { groundColor: action.value });
            case ActionConstants.SET_FLOOR_PLAN:
                return jQuery.extend({}, state, {
                    floorPlan: jQuery.extend({}, state.floorPlan, {
                        visible: action.value
                    })
                });
            case ActionConstants.SET_FLOORPLAN_BRIGHTNESS:
                return jQuery.extend({}, state, {
                    floorPlan: jQuery.extend({}, state.floorPlan, {
                        brightness: action.value
                    })
                });
            case ActionConstants.SET_FLOORPLAN_OPACITY:
                return jQuery.extend({}, state, {
                    floorPlan: jQuery.extend({}, state.floorPlan, {
                        opacity: action.value
                    })
                });
            case ActionConstants.SET_FLOORPLAN_COLOR_REDUCTION:
                return jQuery.extend({}, state, {
                    floorPlan: jQuery.extend({}, state.floorPlan, {
                        colorReduction: action.value
                    })
                });
            case ActionConstants.SET_FLOORPLAN_SIZE:
                return jQuery.extend({}, state, {
                    floorPlan: jQuery.extend({}, state.floorPlan, {
                        width: action.value.width,
                        height: action.value.height
                    })
                });
            case ActionConstants.IMPORT_PROJECT_SETTINGS:
                return jQuery.extend({}, state, action.value);
            case ActionConstants.SET_CURRENCY:
            case ActionConstants.SET_CURRENCY_TEMP:
                return jQuery.extend({}, state, {currency: action.value});
            case ActionConstants.SET_DATA_LANGUAGE:
            case ActionConstants.SET_DATA_LANGUAGE_TEMP:
                return jQuery.extend({}, state, {dataLanguage: action.value});
            case ActionConstants.SET_MEASUREMENT:
            case ActionConstants.SET_MEASUREMENT_TEMP:
                return jQuery.extend({}, state, {unitOfMeasurement: action.value});
            case ActionConstants.SET_FRONT_WALLS:
            case ActionConstants.SET_FRONT_WALLS_TEMP:
                return jQuery.extend({}, state, { frontWalls: action.value});
            case ActionConstants.SET_PRICE_VISIBLITY:
            case ActionConstants.SET_PRICE_VISIBLITY_TEMP:
                return jQuery.extend({}, state, {articleListPrices: action.value});
            default:
                break;
        }
        return state;
    }
}

export let ProjectSettingsStore: ProjectSettingsStoreClass = new ProjectSettingsStoreClass(AppDispatcher);


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/stores/ProjectSettingsStore.ts